import React from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    IconButton,
    Box,
    Typography,
    OutlinedInput,
    InputLabel
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { IAddUpdateSectionItemRequest } from "../../../../model/admin-portal/request/add-update-custom-page-request";
import { PlainTextArea } from "../../../components/plain-text-area";
import DefaultUploadFile from "../../../components/default-upload-file";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface ItemFormProps {
    id: string;
    item: IAddUpdateSectionItemRequest;
    sectionIndex: number;
    itemIndex: number;
    onUpdate: (sectionIndex: number, itemIndex: number, field: string, value: any) => void;
    onRemove: (sectionIndex: number, itemIndex: number) => void;
    getFile: (sectionIndex: number, itemIndex: number) => File | null;
    setFile: (sectionIndex: number, itemIndex: number, file: File | null) => void;
    expanded?: boolean;
    onExpandChange?: (sectionIndex: number, itemIndex: number, expanded: boolean) => void;
}

export const ItemForm: React.FC<ItemFormProps> = ({
    id,
    item,
    sectionIndex,
    itemIndex,
    onUpdate,
    onRemove,
    getFile,
    setFile,
    expanded = false,
    onExpandChange
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        marginBottom: '16px'
    };

    return (
        <Accordion
            ref={setNodeRef}
            style={style}
            sx={{ mb: 2 }}
            expanded={expanded}
            onChange={(_, isExpanded) => onExpandChange?.(sectionIndex, itemIndex, isExpanded)}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1} width="100%">
                    <Box
                        {...attributes}
                        {...listeners}
                        sx={{ cursor: 'grab', display: 'flex' }}
                    >
                        <DragIndicatorIcon color="action" />
                    </Box>
                    <Typography sx={{ flexGrow: 1 }}>
                        {item.title || `Item ${itemIndex + 1}`}
                    </Typography>
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove(sectionIndex, itemIndex);
                        }}
                        size="small"
                        color="error"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Grid container spacing={2}>
                    {/* Title */}
                    <Grid item xs={12}>
                        <InputLabel>Título *</InputLabel>
                        <OutlinedInput
                            size="small"
                            value={item.title || ''}
                            onChange={(e) => onUpdate(sectionIndex, itemIndex, 'title', e.target.value)}
                            fullWidth
                            error={!item.title || item.title.length === 0}
                        />
                    </Grid>

                    {/* Subtitle */}
                    <Grid item xs={12}>
                        <InputLabel>Subtítulo</InputLabel>
                        <OutlinedInput
                            size="small"
                            value={item.subtitle || ''}
                            onChange={(e) => onUpdate(sectionIndex, itemIndex, 'subtitle', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                        <InputLabel>Descrição</InputLabel>
                        <PlainTextArea
                            value={item.description || ''}
                            fullWidth
                            onChange={(e) => onUpdate(sectionIndex, itemIndex, 'description', e.target.value)}
                            rows={3}
                        />
                    </Grid>

                    {/* URL */}
                    <Grid item xs={12}>
                        <InputLabel>URL (Link)</InputLabel>
                        <OutlinedInput
                            size="small"
                            value={item.url || ''}
                            onChange={(e) => onUpdate(sectionIndex, itemIndex, 'url', e.target.value)}
                            fullWidth
                            placeholder="https://..."
                        />
                    </Grid>

                    {/* Image Upload */}
                    <Grid item xs={12}>
                        <InputLabel>Imagem</InputLabel>
                        <DefaultUploadFile
                            id={`section-${sectionIndex}-item-${itemIndex}-image`}
                            file={getFile(sectionIndex, itemIndex) ?? undefined}
                            setFileUpload={(file) => setFile(sectionIndex, itemIndex, file)}
                            extensions={['jpg', 'jpeg', 'png', 'gif']}
                            maxFileSizeMb={5}
                            existingImageUrl={item.imageUrl}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};
